export type SortingOrderType = 'asc' | 'desc';

export interface SortingParameters {
    sort?: string;
    order?: SortingOrderType;
}

export interface AppliedSorting {
    name: string;
    order: SortingOrderType;
}

export enum ProductImageTag {
    REGULAR = 'reg',
    DETAIL = 'dtl',
    SIZE_RELATION = 'srl',
    INFO_GRAPHIC = 'inf',
    LOOK = 'lok',
    USER_GENERATED_CONTENT = 'ugc',
    MOOD = 'mod',
    ENERGY_EFFICIENCY = 'eel',
}

// ProductImageTag and ProductImageTagType need to be kept in sync
export type ProductImageTagType = 'reg' | 'dtl' | 'srl' | 'inf' | 'lok' | 'ugc' | 'mod' | 'eel';

interface SimpleImageData {
    alt_tag?: string;
    path: string;
    seo_url: string;
    tag: ProductImageTagType;
}

interface SimpleMoodImageData extends SimpleImageData {
    tag: ProductImageTag.MOOD;
}

export interface ProductSimpleItem {
    meta: {
        price: number;
        original_price: number;
        available: boolean;
        is_visible?: boolean;
        image_url: string;
        alt_tag: string;
        filter_size?: string;
        delivery_time?: number;
        installments_monthly_price?: number;
        installments_term?: number;
        image?: SimpleImageData;
        mood_image?: SimpleMoodImageData;
    };
}

export interface ProductResponseMeta {
    best_selling_simple_sku?: string;
    cheapest_simple_sku?: string;
    sku: string;
    image_url?: string;
    name: string;
    alt_tag: string;
    brand: string;
    black_friday_top_deal?: boolean;
    cyber_monday_top_deal?: boolean;
    is_sustainable: boolean;
    pdp_url: string;
    soldout?: boolean;
    quantity_badge_number?: number;
    energy_efficiency?: { label?: string; color?: string };
}

export interface ProductResponseSimples {
    [key: string]: ProductSimpleItem;
}

export interface ProductResponse {
    meta: ProductResponseMeta;
    simples: ProductResponseSimples;
}

export enum WishListTrackingAction {
    ADD = 'add',
    REMOVE = 'remove',
}

// WishListTrackingAction and WishListTrackingActionType need to be kept in sync
export type WishListTrackingActionType = WishListTrackingAction.ADD | WishListTrackingAction.REMOVE;

export type WishlistTrackingParameter = {
    action: WishListTrackingActionType;
    sku: string;
    category?: string;
    name?: string;
    brand?: string;
    price?: string;
    label?: string;
    currency?: string;
    url?: string;
};

export type WishlistTrackingFn = (arg: WishlistTrackingParameter) => void;

export interface FormattedImageData {
    path: string;
    seoUrl: string;
    tag: ProductImageTagType;
}

export interface FormattedMoodImageData extends FormattedImageData {
    tag: ProductImageTag.MOOD;
}

export type PlpColorVariants = {
    sku: string;
    name: string;
    pdpUrl: string;
    brand: string;
    isSustainable: boolean;
    price: string;
    originalPrice: string;
    productImage: FormattedImageData;
};

export type PrimaryProductInfo = Pick<ProductInterface, 'name' | 'brand' | 'price' | 'originalPrice' | 'productImage' | 'isSustainable'>

export interface ProductInterface {
    sku: string;
    simpleSku: string;
    baseImage: string;
    name: string;
    altTag: string;
    brand: string;
    blackFriday: boolean;
    cyberMonday: boolean;
    isSustainable: boolean;
    price: string;
    originalPrice: string;
    link: string;
    simplesCount: number;
    quantityBadge: number;
    position: number;
    soldOut: boolean;
    energyEfficiencyLabel?: string;
    clickTrackingFn?: () => void;
    wishListTrackingFn?: WishlistTrackingFn;
    addToWishListExperimentFn?: () => void;
    impressionTrackingFn?: () => void;
    productImage: FormattedImageData;
    productMoodImage?: FormattedMoodImageData;
    bestSellingImage?: FormattedImageData;
    colorVariants?: PlpColorVariants[];
    hotspot?: {
        x: number;
        y: number;
        isAlternative?: boolean;
    };
}
